import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { WindowState } from './types'

import { Bezel } from './bezel'
import { useDesk95Theme } from './theme'

type WindowFrameProps = {
    win: WindowState
    active: boolean
    menu?: string[]
    /** `sheet` is the taller, icon-less chrome the project documents open in. */
    chrome?: 'classic' | 'sheet'
    children: ReactNode
    onFocus: () => void
    onClose: () => void
    onMinimize: () => void
    onTitlePointerDown: (event: ReactPointerEvent) => void
    onGripPointerDown: (event: ReactPointerEvent) => void
}

const stop = (event: ReactPointerEvent) => event.stopPropagation()

const MinimiseGlyph = (): ReactNode => (
    <span className='block h-[2px] w-[7px] translate-y-[3px] bg-current' />
)

const CloseGlyph = (): ReactNode => (
    <svg viewBox='0 0 8 7' width='8' height='7' shapeRendering='crispEdges' aria-hidden='true'>
        <path
            fill='currentColor'
            d='M0 0h2v1H0zM6 0h2v1H6zM1 1h2v1H1zM5 1h2v1H5zM2 2h4v1H2zM3 3h2v1H3zM2 4h4v1H2zM1 5h2v1H1zM5 5h2v1H5zM0 6h2v1H0zM6 6h2v1H6z'
        />
    </svg>
)

export const WindowFrame = ({
    win,
    active,
    menu,
    chrome = 'classic',
    children,
    onFocus,
    onClose,
    onMinimize,
    onTitlePointerDown,
    onGripPointerDown
}: WindowFrameProps): ReactNode => {
    const palette = useDesk95Theme()
    const sheet = chrome === 'sheet'

    const buttonStyle = sheet
        ? { width: 22, height: 20, cursor: 'default' as const }
        : { width: 14, height: 14, cursor: 'default' as const }

    return (
        <Bezel
            className='absolute flex flex-col p-[2px]'
            style={{
                left: win.x,
                top: win.y,
                width: win.w,
                height: win.h,
                zIndex: win.z
            }}
            onPointerDown={onFocus}
            role='dialog'
            aria-label={win.title}
        >
            <div
                className='desk95-handle flex shrink-0 items-center gap-[3px] select-none'
                style={{
                    height: sheet ? 26 : 18,
                    paddingLeft: sheet ? 7 : 2,
                    paddingRight: sheet ? 3 : 2,
                    background: active ? palette.titleActive : palette.titleInactive,
                    color: active ? palette.titleActiveText : palette.titleInactiveText,
                    cursor: 'move'
                }}
                onPointerDown={onTitlePointerDown}
                onDoubleClick={onMinimize}
            >
                {!sheet && (
                    <img src={win.icon} alt='' width={14} height={14} className='desk95-pixel size-[14px] shrink-0' />
                )}

                <span
                    className='flex-1 truncate leading-none font-bold'
                    style={{ fontSize: sheet ? 13 : 11 }}
                >
                    {win.title}
                </span>

                <Bezel
                    thin
                    className='flex items-center justify-center'
                    style={buttonStyle}
                    onPointerDown={stop}
                    onClick={onMinimize}
                    role='button'
                    tabIndex={0}
                    aria-label={`Minimise ${win.title}`}
                    onKeyDown={event => event.key === 'Enter' && onMinimize()}
                >
                    <MinimiseGlyph />
                </Bezel>

                <Bezel
                    thin
                    className='flex items-center justify-center'
                    style={buttonStyle}
                    onPointerDown={stop}
                    onClick={onClose}
                    role='button'
                    tabIndex={0}
                    aria-label={`Close ${win.title}`}
                    onKeyDown={event => event.key === 'Enter' && onClose()}
                >
                    <CloseGlyph />
                </Bezel>
            </div>

            {menu && menu.length > 0 && (
                <div className='flex h-[16px] shrink-0 items-center gap-[10px] px-[3px] text-[11px] leading-none select-none'>
                    {menu.map(item => (
                        <span key={item}>
                            <u>{item.slice(0, 1)}</u>
                            {item.slice(1)}
                        </span>
                    ))}
                </div>
            )}

            <div className='relative min-h-0 flex-1 overflow-hidden'>
                {children}
            </div>

            <div
                className='desk95-handle absolute right-[2px] bottom-[2px] size-[16px]'
                style={{ cursor: 'nwse-resize' }}
                onPointerDown={onGripPointerDown}
                aria-hidden='true'
            >
                <svg viewBox='0 0 16 16' width='16' height='16' shapeRendering='crispEdges'>
                    <path fill={palette.highlight} d='M11 14h1v1h-1zM14 11h1v1h-1zM7 14h1v1H7zM14 7h1v1h-1zM11 10h1v1h-1zM10 11h1v1h-1z' />
                    <path fill={palette.shadow} d='M12 14h2v1h-2zM14 12h1v2h-1zM8 14h2v1H8zM14 8h1v2h-1zM12 10h2v1h-2zM10 12h1v2h-1z' />
                </svg>
            </div>
        </Bezel>
    )
}
